// Core imports
import React, { Fragment, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { t } from 'i18next';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

// Component imports
import { Button, Checkbox, Col, Collapse, Divider, Empty, Modal, Row, Spin, Typography } from 'antd';
import FormSectionHeader from '../UI/FormSectionHeader';
import { PlusIcon } from '../Icons/PlusIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import NlpPipelineStatusTag from '../UI/NlpPipelineStatusTag';

// Misc imports
import { useFetchNlpPipelinesQuery, useTrainNlpPipelineMutation } from '../../store/nlpPipelines/nlpPipelinesApi';
import { TEST_SET_TABS } from '../../constants/testSets';
import { useLazyTestSetResultQuery, useRunTestSetMutation } from '../../store/testSets/testSetsApi';
import { useTestSetNavigation } from '../../utils/hooks/useTestSetsNavigation';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { API_NLP_SERVICE_URL } from '../../constants/env';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';

const NLP_STATUS = {
  READY: 'READY',
  TRAINING: 'TRAINING',
  FAILED: 'FAILED'
};

const TestSetRunTestForm = forwardRef(({
  activeTestSetId = null,
  setIsLoading = () => false,
  ...props
}, ref) => {
  const { orgId, tenantId } = useParams();
  const { navigateToTestSet } = useTestSetNavigation();

  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const [trainNlpPipeline] = useTrainNlpPipelineMutation({ fixedCacheKey: 'training-nlp-shared' });
  const [runTestSet, { isLoading: isRunningTestSet }] = useRunTestSetMutation({ fixedCacheKey: 'test-set-results' });
  const [fetchTestSetResult] = useLazyTestSetResultQuery();
  const { data: nlpPipelines, isFetching: isFetchingNlpPipelines, refetch: fetchNlpPipelines } = useFetchNlpPipelinesQuery(
    { orgId, tenantId },
    { selectFromResult: ({ data }) => {
      const itemsMap = new Map();

      data?.items?.forEach(item => {
        itemsMap.set(item?.id, item);
      });

      return {
        data: {
          ...data,
          itemsMap
        }
      };
    } }
  );

  const [isNLPSelectionModalOpen, setIsNLPSelectionModalOpen] = useState(false);
  const [checkedNLPList, setCheckedNLPList] = useState([]);
  const [indeterminate, setIndeterminate] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [visibleNLPList, setVisibleNLPList] = useState([]);
  const [trainingList, setTrainingList] = useState([]);

  useImperativeHandle(ref, () => ({
    renderFooter: ({ disabled }) => renderEditFooter(disabled)
  }));

  const renderEditFooter = useCallback((disabled) => {
    return (
      <Row justify="end" gutter={8}>
        <Col>
          <Button
            type="primary"
            onClick={async () => {
              const testSetResult = await runTestSet({
                orgId,
                tenantId,
                nlpTestId: activeTestSetId,
                nlpPipelines: visibleNLPList,
                ...bot?.systemSettings?.nlpService === 'HBF_NLP' && { apiUrl: API_NLP_SERVICE_URL }
              }).unwrap();

              if (testSetResult?.id) {
                const isSuccessfull = await fetchTestSetResult({ orgId: orgId, tenantId: tenantId, nlpTestId: activeTestSetId, runId: testSetResult?.id }).unwrap();

                if(isSuccessfull) {
                  navigateToTestSet(activeTestSetId, TEST_SET_TABS.TEST_RESULTS.key, null, false);
                }
              }
            }}
            size="large"
            loading={isRunningTestSet}
            icon={TEST_SET_TABS.TEST_RUN.renderIcon({ className: 'mt-1' })}
            disabled={isRunningTestSet || visibleNLPList?.length === 0 || trainingList.length > 0 || disabled}
          >
            {t('Run Test')}
          </Button>
        </Col>
      </Row>
    );
  }, [isRunningTestSet, visibleNLPList, trainingList]);

  useEffect(() => {
    let trainIntervalId = null;
    if (!trainingList.length && nlpPipelines?.items?.length > 0) {
      const trainingPipelines = nlpPipelines?.items?.filter(item => item.status === NLP_STATUS.TRAINING && checkedNLPList.includes(item?.id))?.map(item => item.id);
      if(trainingPipelines?.length > 0) {
        setTrainingList(trainingPipelines);
      }
    }

    if (trainingList?.length > 0) {
      trainIntervalId = setInterval(async () => {
        const pipelines = await fetchNlpPipelines()?.unwrap();

        if(trainingList?.length === 0 ) {
          clearInterval(trainIntervalId);
          return;
        }

        trainingList?.forEach(pipelineId => {
          const status = pipelines?.items?.find(item => item.id === pipelineId)?.status;
          if (
            status === NLP_STATUS.FAILED
          || status === NLP_STATUS.READY
          ) {
            setTrainingList(prev => {
              return prev.filter(id => id !== pipelineId);
            });
          }
        });
      }, 3000);
    }

    if(trainIntervalId && trainingList?.length === 0) {
      clearInterval(trainIntervalId);
    }

    return () => {
      clearInterval(trainIntervalId);
    };
  }, [trainingList, nlpPipelines?.items, checkedNLPList]);

  const onNLPCheckboxChange = (nlpList) => {
    setCheckedNLPList(nlpList);
    setIndeterminate(!!nlpList.length && nlpList.length < nlpPipelines?.items?.length);
    setCheckAll(nlpList.length === nlpPipelines?.items?.length);
  };

  const onCheckAllNLPsChange = (e) => {
    setCheckedNLPList(e.target.checked ? nlpPipelines?.items?.map(item => item?.id) : []);
    setIndeterminate(false);
    setCheckAll(e.target.checked);
  };

  const renderNLPSelectionModalContent = () => {
    if (isFetchingNlpPipelines && isEmptyArray(nlpPipelines?.items)) {
      return (
        <Col xs={24}>
          <Spin spinning tip={t('Fetching NLP Systems')} />
        </Col>
      );
    }

    if (isEmptyArray(nlpPipelines?.items)) {
      return (
        <Col xs={24}>
          <Empty
            description={t('No NLP Systems available')}
          />
        </Col>
      );
    }

    return (
      <Fragment>
        <Col xs={24} hidden={nlpPipelines?.items?.length === 0}>
          <Checkbox
            indeterminate={indeterminate}
            onChange={onCheckAllNLPsChange}
            checked={checkAll}
          >
            {t('Select All')}
          </Checkbox>
        </Col>

        <Divider className="my-2"/>

        <Col xs={24}>
          <Checkbox.Group
            value={checkedNLPList}
            onChange={onNLPCheckboxChange}
            options={nlpPipelines?.items?.map(item => ({
              label: item?.name,
              value: item?.id
            }))}
            className="scrollbar-custom scrollbar-custom-nohide overflow-y-auto d-flex flex-column"
            style={{
              maxHeight: '200px'
            }}
          />
        </Col>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Collapse defaultActiveKey={['2']} ghost>
        <Collapse.Panel
          showArrow={false}
          collapsible="icon"
          header={<FormSectionHeader title={t('NLP Selection')} uppercase collapsible={false} />}
          key="2"
        >
          <Row gutter={8}>
            <Col xs={24}>
              <Button
                size="large"
                disabled={isFetchingNlpPipelines || isRunningTestSet}
                onClick={() => {
                  setIsNLPSelectionModalOpen(true);
                }}
                loading={isFetchingNlpPipelines}
              >
                <PlusIcon /> {t('Add NLPs')}
              </Button>
            </Col>

            <Col xs={24} className="mt-3">
              <Row gutter={8}>
                {
                  visibleNLPList?.length === 0 && (
                    <Col xs={24} className="ant-card bg-layout mb-2 shadow-none">
                      <Row className="p-3 d-flex align-items-center justify-content-center">
                        <Col xs={24} className="d-flex align-items-center justify-content-center">
                          <Typography.Text>{t('No NLP System selected')}</Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  )
                }

                {
                  visibleNLPList.map((id) => (
                    <Col xs={24} className="ant-card bg-layout mb-2 shadow-none">
                      <Row className="p-3 d-flex align-items-center">
                        <Col xs={18}>
                          <Typography.Text>{nlpPipelines?.itemsMap?.get(id)?.name}</Typography.Text>
                        </Col>

                        <Col xs={6} className="justify-content-end d-flex">
                          <Button
                            type="text"
                            className="pointer-events-none"
                          >
                            <NlpPipelineStatusTag pipelineStatus={nlpPipelines?.itemsMap?.get(id)?.status} />
                          </Button>

                          <Button
                            className="min-w-50"
                            hidden={nlpPipelines?.itemsMap?.get(id)?.status === NLP_STATUS.READY}
                            type="primary"
                            disabled={isRunningTestSet}
                            onClick={async () => {
                              const isTraining = await trainNlpPipeline({
                                orgId,
                                tenantId,
                                nlpPipelineId: id,
                                ...bot?.systemSettings?.nlpService === 'HBF_NLP' && { apiUrl: API_NLP_SERVICE_URL }
                              }).unwrap();
                              if(isTraining) {
                                setTrainingList(prev => {
                                  return [...prev, id];
                                });
                                await fetchNlpPipelines();
                              }
                            }}
                            loading={trainingList?.includes(id)}
                          >
                            {trainingList?.includes(id) ? t('Training') : t('Train')}
                          </Button>

                          <Button
                            onClick={() => {
                              setVisibleNLPList(prev => {
                                return prev.filter(prevId => id !== prevId);
                              });

                              setCheckedNLPList(prev => {
                                const newChecked = [...prev.filter(prevId => id !== prevId)];

                                if(newChecked.length > 0 && checkAll) {
                                  setIndeterminate(true);
                                }

                                if(newChecked.length === 0) {
                                  setIndeterminate(false);
                                }

                                if(checkAll) {
                                  setCheckAll(false);
                                }

                                return newChecked;
                              });

                              setTrainingList(prev => {
                                return prev.filter(prevId => id !== prevId);
                              });
                            }}
                            type="text"
                            disabled={isRunningTestSet}
                            className="text-primary ml-2"
                            shape="circle"
                            icon={<DeleteIcon />}
                          />
                        </Col>
                      </Row>
                    </Col>
                  ))
                }
              </Row>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

      <Modal
        open={isNLPSelectionModalOpen}
        onCancel={() => {
          setCheckedNLPList((prev) => {
            if((visibleNLPList?.length !== nlpPipelines?.items?.length)) {
              if(!indeterminate && visibleNLPList?.length > 0) {
                setIndeterminate(true);
              }
              setCheckAll(false);
            }

            if(visibleNLPList?.length === nlpPipelines?.items?.length) {
              setIndeterminate(false);
              setCheckAll(true);
            }

            return [...visibleNLPList];
          });

          setIsNLPSelectionModalOpen(false);
        }}
        onOk={() => {
          setVisibleNLPList([...checkedNLPList]);
          setIsNLPSelectionModalOpen(false);
        }}
        className="ant-modal-p-0"
        title={t('Test available NLPs')}
      >
        <Row className="px-5 py-3">
          {renderNLPSelectionModalContent()}
        </Row>
      </Modal>
    </Fragment>
  );
});

TestSetRunTestForm.propTypes = {
  activeTestSetId: PropTypes.oneOfType(['string', null]),
  setIsLoading: PropTypes.func
};

export default TestSetRunTestForm;
